/**
 * Parts, filters, and their identifiers — the ONE author of what a part is,
 * what a filter is, and where each lives (FRIDGE-333 · FRIDGE-389 ·
 * FRIDGE-396, owner rulings 08/16–08/20/2026).
 *
 * The four identifiers, in the order they are always named — FIN, FPN, FFN,
 * FIS (settled 08/17/2026, FRIDGE-391 + FRIDGE-396; pinned by
 * tests/identifier-naming-order.test.ts):
 *   (FIN) Fridge.com Identification Number — the permanent per-product
 *         canonical identifier of an APPLIANCE (`FIN-0042893`). An identifier,
 *         not a rating. Minted once on canonical_products, never reused.
 *   (FPN) Fridge.com Part Number — the permanent per-part identifier of a PART
 *         (`FPN-0000001`). Minted once on the part's products row, never
 *         reused. A part carries an FPN and never a FIN.
 *   (FFN) Fridge.com Filter Number — the permanent per-filter identifier of a
 *         FILTER (`FFN-0000001`). Minted once on the filter's products row,
 *         never reused. A filter carries an FFN and never an FPN.
 *   (FIS) Fridge.com Intelligence Score — a 0–100 score; in shipped code it
 *         ranks locations. Never mixed with any identifier.
 *
 * A FILTER IS NOT A PART — owner ruling 08/17/2026 (FRIDGE-396). Three
 * product families, three identifiers, three URL roots:
 *
 *   family     types                                   identifier   root
 *   appliance  everything not below                    FIN          /products/
 *   part       gasket · sensor · part · ice-maker-kit  FPN          /part/
 *   filter     water- · air- · condenser- ·            FFN          /filter/
 *              ice-machine- · inline-filter
 *
 * Both item roots are SINGULAR — owner ruling 08/17/2026 (FRIDGE-389), for
 * SEO variation coverage (`/part/frigidaire-refrigerator-parts`). The plural
 * forms this module authored until 08/20/2026 (`/parts/<slug>`) 301 to the
 * singular homes through redirects.csv, built by
 * scripts/build-parts-redirects.ts — per-URL, never a pattern.
 *
 * WHAT A PART IS / WHAT A FILTER IS
 * A products row whose `type` is in TRUE_PART_TYPES or FILTER_PRODUCT_TYPES
 * respectively. These lists are the whole definition — the hubs, the item
 * routes, the sitemaps, the redirect map, the breadcrumb builder and the
 * FPN/FFN mint triggers in supabase/migrations all key on the SAME sets (the
 * migrations carry their own copies inside public.is_part_product_type() and
 * public.is_filter_product_type(); tests/parts-fpn.test.ts pins them equal).
 * PART_PRODUCT_TYPES remains the union of the two — the "not an appliance"
 * test — because a dozen call sites (sitemap exclusion, PDP family gate,
 * collection safety) genuinely mean "part OR filter".
 *
 * 'part' is the FRIDGE-277 type staged in
 * 20260816120000_products_type_parts_hardware.sql: the constraint value
 * exists on apply, and the seven mis-typed rows move onto it in the staged
 * retype migration. The product classifier (lib/product-classifier) does NOT
 * emit 'part' — parts and filters enter the catalog by curation, and the
 * classifier's parts rejection guards imports exactly as before (its
 * cooling_part category IS this module's filter family).
 *
 * 'ice-maker-kit' is the FRIDGE-57 type (recommendation 13 of the taxonomy
 * brief, approved 10/20/2026), staged in
 * 20261021040000_ice_maker_kit_part_type.sql: a factory add-on or replacement
 * ice maker that fits a refrigerator model is a PART (FPN); a complete,
 * stand-alone ice maker stays an APPLIANCE (FIN, type 'ice-maker'). The
 * constraint value and the two database functions exist on apply; the six
 * OEM kits named on FRIDGE-34 move onto the type in the prepared s5
 * transaction (research/cards/fridge-57-2026-10-21). Until the apply, the
 * classifier's constraint-safe bucket for the value is 'part'
 * (lib/product-classifier/vocabularies.ts), never an appliance. Its node
 * under /parts is lib/taxonomy/tree.ts PART_TYPE_NODES.
 *
 * Pure and dependency-free on purpose (mirrors lib/breadcrumbs.ts): imported
 * by client bundles, server templates, scripts and tests alike.
 */

/** Every products.type value that is a true PART — (FPN) carriers, homed at /part/. */
export const TRUE_PART_TYPES = [
  'gasket',
  'sensor',
  'part',
  'ice-maker-kit',
] as const

/** Every products.type value that is a FILTER — (FFN) carriers, homed at /filter/. */
export const FILTER_PRODUCT_TYPES = [
  'water-filter',
  'air-filter',
  'condenser-filter',
  'ice-machine-filter',
  'inline-filter',
] as const

/**
 * The union: every products.type value that is NOT an appliance. Order is
 * documentation, not priority. Call sites that mean "leaves the appliance
 * PDP family" key on this; call sites that route or mint key on the two
 * specific sets above.
 */
export const PART_PRODUCT_TYPES = [
  ...FILTER_PRODUCT_TYPES,
  ...TRUE_PART_TYPES,
] as const

export type TruePartType = (typeof TRUE_PART_TYPES)[number]
export type FilterProductType = (typeof FILTER_PRODUCT_TYPES)[number]
export type PartProductType = (typeof PART_PRODUCT_TYPES)[number]

const TRUE_PART_SET: ReadonlySet<string> = new Set<string>(TRUE_PART_TYPES)
const FILTER_SET: ReadonlySet<string> = new Set<string>(FILTER_PRODUCT_TYPES)
const PART_TYPE_SET: ReadonlySet<string> = new Set<string>(PART_PRODUCT_TYPES)

/** True when a products.type value names a true part (case- and whitespace-tolerant). */
export function isTruePartType(type: string | null | undefined): type is TruePartType {
  if (typeof type !== 'string') return false
  return TRUE_PART_SET.has(type.trim().toLowerCase())
}

/** True when a products.type value names a filter (case- and whitespace-tolerant). */
export function isFilterType(type: string | null | undefined): type is FilterProductType {
  if (typeof type !== 'string') return false
  return FILTER_SET.has(type.trim().toLowerCase())
}

/** True when a products.type value names a part OR a filter — i.e. not an appliance. */
export function isPartType(type: string | null | undefined): type is PartProductType {
  if (typeof type !== 'string') return false
  return PART_TYPE_SET.has(type.trim().toLowerCase())
}

/**
 * The site-relative root of the parts family: the hub and every part page sit
 * under it. PLURAL — owner ruling 08/22/2026 (FRIDGE-501), superseding the
 * 08/17/2026 singular ruling (FRIDGE-389): "change absolutely all /part to
 * /parts/". The old singular root 308s here, one hop.
 */
export const PARTS_ROUTE_ROOT = '/parts'

/**
 * The site-relative root of the filter family. PLURAL — owner ruling
 * 08/22/2026 (FRIDGE-500), superseding the 08/17/2026 singular ruling
 * (FRIDGE-396): "filters home is /filters/ and filters product is
 * /filters/[slug]". This RETIRES the singular-store / plural-editorial split:
 * /filters/[slug] now resolves an editorial hub first (hub_pages, 15 rows)
 * and a filter product second — the recalls-route dual-resolution pattern.
 * The old singular root 308s here, one hop.
 */
export const FILTERS_ROUTE_ROOT = '/filters'

/**
 * The three route families the ONE product renderer serves
 * (app/products/[slug]/page.tsx): `products` is /products/[slug] — appliances,
 * FIN-anchored; `parts` is /part/[slug] — true parts, FPN-anchored; `filters`
 * is /filter/[slug] — filters, FFN-anchored. Which family a row belongs to is
 * decided by its type, never by the URL it was requested under: a row under
 * the wrong root redirects home, an appliance under either item root is a 404.
 *
 * NOTE `/filters/<slug>` carries TWO populations since the 08/22/2026 plural
 * ruling (FRIDGE-500): the EDITORIAL filter-hub family (hub_pages rows,
 * registry row 13 — resolved FIRST) and the filter PRODUCT family (resolved
 * second) — the same dual-resolution shape as /recalls/[slug]. The retired
 * singular-store / plural-editorial split lives only in history now.
 */
export type ProductPageFamily = 'products' | 'parts' | 'filters'

/** The family a product row belongs to, from its type alone. */
export function familyForType(type: string | null | undefined): ProductPageFamily {
  if (isFilterType(type)) return 'filters'
  if (isTruePartType(type)) return 'parts'
  return 'products'
}

/** The item root a family renders under. */
export function familyRoot(family: ProductPageFamily): string {
  if (family === 'filters') return FILTERS_ROUTE_ROOT
  if (family === 'parts') return PARTS_ROUTE_ROOT
  return '/products'
}

/**
 * The page a product's slug renders at, decided ONLY by its type:
 *   filter        → `/filter/<slug>`
 *   true part     → `/part/<slug>`
 *   anything else → `/products/<slug>`
 * Returns null when there is no slug — a caller must not render a link.
 */
export function productPagePath(product: {
  type?: string | null
  seo_slug?: string | null
  seoSlug?: string | null
  slug?: string | null
}): string | null {
  const slug = firstSlug(product.seo_slug, product.seoSlug, product.slug)
  if (!slug) return null
  return `${familyRoot(familyForType(product.type))}/${slug}`
}

/** `/part/<slug>` for a slug already known to be a true part's — never for a filter or an appliance. */
export function partPagePath(slug: string): string {
  return `${PARTS_ROUTE_ROOT}/${slug}`
}

/** `/filter/<slug>` for a slug already known to be a filter's — never for a part or an appliance. */
export function filterPagePath(slug: string): string {
  return `${FILTERS_ROUTE_ROOT}/${slug}`
}

function firstSlug(...candidates: Array<string | null | undefined>): string | null {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0 && !c.includes('/')) return c.trim()
  }
  return null
}

// ---------------------------------------------------------------------------
// The identifier values themselves — FPN on parts, FFN on filters
// ---------------------------------------------------------------------------

/**
 * `FPN-` + seven zero-padded digits — the same shape as a FIN (`FIN-0042893`),
 * so the two read as siblings and never as the same thing. Minted in the
 * database by public.mint_fpn() from public.products_fpn_seq (gap-tolerant,
 * never reused); application code never invents one.
 */
export const FPN_REGEX = /^FPN-\d{7}$/

/** `FFN-` + seven zero-padded digits — the filter sibling of the FPN shape. */
export const FFN_REGEX = /^FFN-\d{7}$/

/** True iff the value is a well-formed FPN. */
export function isValidFpn(value: unknown): value is string {
  return typeof value === 'string' && FPN_REGEX.test(value)
}

/** True iff the value is a well-formed FFN. */
export function isValidFfn(value: unknown): value is string {
  return typeof value === 'string' && FFN_REGEX.test(value)
}

/**
 * The FPN a part surface may display: the stored value when it is well-formed,
 * otherwise null (the badge and the schema self-hide — Real-or-Hidden). Until
 * the FPN migration is applied the products row carries no `fpn` column and
 * every part renders without an identifier rather than with an invented one.
 */
export function displayFpn(value: unknown): string | null {
  return isValidFpn(value) ? value : null
}

/** The FFN a filter surface may display — same Real-or-Hidden contract as displayFpn. */
export function displayFfn(value: unknown): string | null {
  return isValidFfn(value) ? value : null
}

/** URL/slug form of an FPN, mirroring formatFinForUrl: "FPN-0000012" → "fpn0000012". */
export function formatFpnForUrl(fpn: string | null | undefined): string | null {
  if (!fpn) return null
  const m = fpn.trim().match(/^FPN-(\d{7})$/)
  return m ? `fpn${m[1]}` : null
}

/** URL/slug form of an FFN: "FFN-0000012" → "ffn0000012". */
export function formatFfnForUrl(ffn: string | null | undefined): string | null {
  if (!ffn) return null
  const m = ffn.trim().match(/^FFN-(\d{7})$/)
  return m ? `ffn${m[1]}` : null
}
